/**
 * Common utilities for pages
 * Auth tokens use sessionStorage (cleared when tab closes) to reduce XSS token-theft window.
 * User profile & roles are cached in-memory + sessionStorage for the tab lifetime.
 */
import axios from 'axios';

export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory cache for auth session (survives navigations, cleared on hard reload)
let memAuth = { token: null, user: null, roles: null };

// Rehydrate from sessionStorage on module load
try {
  const t = sessionStorage.getItem('auth_token');
  const u = sessionStorage.getItem('auth_user');
  const r = sessionStorage.getItem('auth_roles');
  memAuth = {
    token: t || null,
    user: u ? JSON.parse(u) : null,
    roles: r ? JSON.parse(r) : null,
  };
} catch { /* ignore */ }

function persist(token, user, roles) {
  memAuth = { token, user, roles };
  try {
    if (token) sessionStorage.setItem('auth_token', token);
    else sessionStorage.removeItem('auth_token');
    if (user) sessionStorage.setItem('auth_user', JSON.stringify(user));
    else sessionStorage.removeItem('auth_user');
    if (roles) sessionStorage.setItem('auth_roles', JSON.stringify(roles));
    else sessionStorage.removeItem('auth_roles');
  } catch { /* private mode fallback: memory-only */ }
}

// Attach bearer token to every axios request automatically
axios.interceptors.request.use((config) => {
  if (memAuth.token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${memAuth.token}`;
  }
  return config;
});

// Auth
export const authApi = {
  async login(email, password) {
    const res = await axios.post(`${API_URL}/auth/login`, { email, password }, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    });
    if (res.data?.success) {
      persist(res.data.data.token, res.data.data.user, res.data.data.roles || []);
    }
    return res.data;
  },
  async register(name, email, password) {
    const res = await axios.post(`${API_URL}/auth/register`, { name, email, password }, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    });
    if (res.data?.success) {
      persist(res.data.data.token, res.data.data.user, res.data.data.roles || []);
    }
    return res.data;
  },
  logout() {
    persist(null, null, null);
    // Legacy cleanup - remove any old localStorage entries from earlier versions
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_roles');
    } catch { /* ignore */ }
  },
  getToken() {
    return memAuth.token;
  },
  getUser() {
    return memAuth.user;
  },
  getRoles() {
    return memAuth.roles || [];
  },
  isAuthenticated() {
    return !!memAuth.token;
  },
  isAdmin() {
    const roles = memAuth.roles || [];
    return roles.some((r) => (typeof r === 'string' ? r : r.name) === 'admin');
  },
  isExhibitor() {
    const roles = memAuth.roles || [];
    return roles.some((r) => (typeof r === 'string' ? r : r.name) === 'exhibitor');
  },
};

// Alias for consumer components that import { auth }
export const auth = authApi;

// Event countdown
export function formatCountdown(targetDate) {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    expired: false,
  };
}
